/**
 * The one way a list in the console is searched and paged (the API's listQuery.ts is the other half).
 *
 * `useListSearch` holds the words typed and the words searched (debounced 300 ms, so a keystroke is not
 * a request). `usePagedList` loads the first page for a query, keeps the pages "Show more" added, starts
 * over when the query changes, and lets a poll refresh the first page only. `ShowMore`, `listCount` and
 * `NoMatch` are the three pieces every list shows: the button, the count line, and the empty answer that
 * says what was searched, with a Clear action.
 */

import { Clock } from '@gravity-ui/icons';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { problemText } from './api/client.ts';
import { Button, Empty, ListBlock } from './components/ui.tsx';
import type { ArtTone } from './components/ui/Art.tsx';
import { useLoad } from './hooks.ts';

export const SEARCH_DEBOUNCE_MS = 300;

/** The search box's words: `typed` follows every keystroke, `q` is what the list asks the server for. */
export function useListSearch(delayMs = SEARCH_DEBOUNCE_MS) {
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQ(typed.trim()), delayMs);
    return () => clearTimeout(t);
  }, [typed, delayMs]);
  return { typed, setTyped, q, clear: () => (setTyped(''), setQ('')) };
}

/** What a paged route answers: the rows of the page, where the next one starts, and anything else it counts. */
export interface PagedAnswer<T, X = object> {
  items: T[];
  next?: string | undefined;
  extra?: X;
}

export function usePagedList<T, X = object>(load: (cursor: string | undefined) => Promise<PagedAnswer<T, X>>, query: unknown, opts: { intervalMs?: number; keyOf: (row: T) => string }) {
  const first = useLoad(() => load(undefined), [JSON.stringify(query)], opts.intervalMs);
  const queryKey = JSON.stringify(query);
  // Pages after the first belong to the query they were read under; another query starts again from page one.
  const [more, setMore] = useState<{ key: string; rows: T[]; next: string | undefined }>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const loader = useRef(load);
  useEffect(() => {
    loader.current = load;
  });
  const mine = more?.key === queryKey ? more : undefined;
  const cursor = mine ? mine.next : first.data?.next;
  const showMore = useCallback(() => {
    if (!cursor) return;
    setBusy(true);
    loader
      .current(cursor)
      .then((a) => {
        setMore((m) => ({ key: queryKey, rows: [...(m?.key === queryKey ? m.rows : []), ...a.items], next: a.next }));
        setError(undefined);
      })
      .catch((e: unknown) => setError(problemText(e)))
      .finally(() => setBusy(false));
  }, [cursor, queryKey]);
  const seen = new Set<string>();
  const items: T[] = [];
  for (const row of [...(first.data?.items ?? []), ...(mine?.rows ?? [])]) {
    const k = opts.keyOf(row);
    if (seen.has(k)) continue;
    seen.add(k);
    items.push(row);
  }
  return { items, hasMore: Boolean(cursor), showMore, loadingMore: busy, loaded: Boolean(first.data), loading: first.loading, error: error ?? first.error, extra: first.data?.extra, reload: first.reload };
}

/** "Show more" (or "Show older" for a record by time) under the last row of a list. */
export function ShowMore({ onPress, pending, older }: { onPress: () => void; pending?: boolean; older?: boolean }) {
  return (
    <ListBlock>
      <Button size="sm" variant="ghost" isPending={pending} onPress={onPress}>
        <Clock className="size-4" />
        {older ? 'Show older' : 'Show more'}
      </Button>
    </ListBlock>
  );
}

/** The count line of a list: "12 people", "Showing 50 of 214", or "Showing 50 of many" when the total is not known. */
export function listCount(shown: number, noun: { one: string; many: string }, opts: { total?: number; hasMore?: boolean; searched?: boolean } = {}): string {
  const word = (n: number) => `${n.toLocaleString()} ${n === 1 ? noun.one : noun.many}`;
  if (opts.total !== undefined && !opts.hasMore && !opts.searched) return word(opts.total);
  if (opts.hasMore) return opts.total !== undefined && !opts.searched ? `Showing ${shown.toLocaleString()} of ${opts.total.toLocaleString()}` : `Showing ${shown.toLocaleString()} of many`;
  return word(shown);
}

/** The empty answer to a search or a filter: what was searched, and a way back. */
export function NoMatch({ tone, icon, q, filtered, noun, onClear }: { tone: ArtTone; icon: ReactNode; q: string; filtered?: boolean; noun: string; onClear: () => void }) {
  return (
    <Empty
      tone={tone}
      icon={icon}
      compact
      title={q ? `No ${noun} match "${q}"` : `No ${noun} match the filters`}
      body={q && filtered ? 'Nothing fits the words and the filters together.' : 'Try fewer words, or clear the search.'}
      action={
        <Button size="sm" variant="secondary" onPress={onClear}>
          Clear
        </Button>
      }
    />
  );
}
